import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { FiSave, FiTruck, FiPackage } from "react-icons/fi";
import toast from "react-hot-toast";

const EMPTY_FORM = {
    mode: "flat",          // "flat" | "perBook" | "free"
    flatRate: "5.99",
    oneBookRate: "4.99",
    twoPlusRate: "7.99",
    freeThreshold: "0",    // 0 = no free shipping, >0 = free if subtotal >= threshold
    freeShippingEnabled: false,
    localPickupEnabled: false,
    localPickupLabel: "Local Pickup (Puerto Rico)",
};

const MODE_INFO = {
    flat:    "Charge a single flat rate regardless of quantity.",
    perBook: "Charge different rates based on number of books in the order.",
    free:    "Offer free shipping to all customers.",
};

export default function AdminShipping() {
    const [form, setForm] = useState(EMPTY_FORM);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        async function load() {
            try {
                const snap = await getDoc(doc(db, "siteConfig", "shipping"));
                if (snap.exists()) setForm(f => ({ ...EMPTY_FORM, ...snap.data() }));
            } catch { toast.error("Could not load shipping settings."); }
            setLoading(false);
        }
        load();
    }, []);

    function handleChange(e) {
        const { name, value, type, checked } = e.target;
        setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    }

    async function save() {
        setSaving(true);
        try {
            await setDoc(doc(db, "siteConfig", "shipping"), form);
            toast.success("Shipping settings saved!");
        } catch { toast.error("Failed to save."); }
        setSaving(false);
    }

    if (loading) return <div className="admin-loading">Loading…</div>;

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 24 }}>
                <button className="admin-btn-primary" onClick={save} disabled={saving}>
                    <FiSave size={16} /> {saving ? "Saving…" : "Save Settings"}
                </button>
            </div>

            {/* Shipping Mode */}
            <div className="admin-card" style={{ padding: 28, marginBottom: 24 }}>
                <p style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", color: "#888", letterSpacing: 0.5, marginBottom: 20 }}>
                    Shipping Rate Mode
                </p>
                <p style={{ fontSize: 13, color: "#888", marginBottom: 20 }}>{MODE_INFO[form.mode]}</p>

                <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 28 }}>
                    {[
                        { value: "flat",    icon: FiTruck,   label: "Flat Rate",      desc: "One price for all orders" },
                        { value: "perBook", icon: FiPackage, label: "Per Book Qty",   desc: "Different price by quantity" },
                        { value: "free",    icon: FiTruck,   label: "Free Shipping",  desc: "Always free for everyone" },
                    ].map(opt => (
                        <label key={opt.value} style={{
                            flex: "1 1 200px", cursor: "pointer",
                            border: `2px solid ${form.mode === opt.value ? "var(--green-primary)" : "#eee"}`,
                            borderRadius: 12, padding: "16px 20px",
                            background: form.mode === opt.value ? "#f0faf5" : "white",
                            display: "flex", alignItems: "center", gap: 14, transition: "all 0.2s"
                        }}>
                            <input type="radio" name="mode" value={opt.value} checked={form.mode === opt.value} onChange={handleChange} style={{ display: "none" }} />
                            <opt.icon size={22} color={form.mode === opt.value ? "var(--green-primary)" : "#aaa"} />
                            <div>
                                <p style={{ fontWeight: 700, margin: 0 }}>{opt.label}</p>
                                <p style={{ fontSize: 12, color: "#888", margin: 0 }}>{opt.desc}</p>
                            </div>
                        </label>
                    ))}
                </div>

                {/* Flat Rate */}
                {form.mode === "flat" && (
                    <div className="admin-form-grid">
                        <div className="admin-form-field">
                            <label>Flat Shipping Rate ($)</label>
                            <input name="flatRate" type="number" step="0.01" min="0" value={form.flatRate} onChange={handleChange} placeholder="5.99" />
                        </div>
                    </div>
                )}

                {/* Per-Book Rate */}
                {form.mode === "perBook" && (
                    <div className="admin-form-grid">
                        <div className="admin-form-field">
                            <label>1 Book — Rate ($)</label>
                            <input name="oneBookRate" type="number" step="0.01" min="0" value={form.oneBookRate} onChange={handleChange} placeholder="4.99" />
                        </div>
                        <div className="admin-form-field">
                            <label>2+ Books — Rate ($)</label>
                            <input name="twoPlusRate" type="number" step="0.01" min="0" value={form.twoPlusRate} onChange={handleChange} placeholder="7.99" />
                        </div>
                    </div>
                )}
            </div>

            {/* Free Shipping Threshold */}
            <div className="admin-card" style={{ padding: 28, marginBottom: 24 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                    <div>
                        <p style={{ fontWeight: 700, fontSize: 15, margin: 0 }}>Free Shipping Threshold</p>
                        <p style={{ color: "#888", fontSize: 13, margin: "4px 0 0" }}>Customers get free shipping when their subtotal reaches a certain amount.</p>
                    </div>
                    <label className="toggle-switch">
                        <input type="checkbox" name="freeShippingEnabled" checked={form.freeShippingEnabled} onChange={handleChange} />
                        <span className="toggle-slider"></span>
                    </label>
                </div>
                {form.freeShippingEnabled && (
                    <div className="admin-form-grid">
                        <div className="admin-form-field">
                            <label>Free Shipping If Subtotal Exceeds ($)</label>
                            <input name="freeThreshold" type="number" step="0.01" min="0" value={form.freeThreshold} onChange={handleChange} placeholder="50.00" />
                        </div>
                    </div>
                )}
            </div>

            {/* Local Pickup */}
            <div className="admin-card" style={{ padding: 28 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                    <div>
                        <p style={{ fontWeight: 700, fontSize: 15, margin: 0 }}>Local Pickup</p>
                        <p style={{ color: "#888", fontSize: 13, margin: "4px 0 0" }}>Allow customers to pick up orders locally (no shipping cost).</p>
                    </div>
                    <label className="toggle-switch">
                        <input type="checkbox" name="localPickupEnabled" checked={form.localPickupEnabled} onChange={handleChange} />
                        <span className="toggle-slider"></span>
                    </label>
                </div>
                {form.localPickupEnabled && (
                    <div className="admin-form-grid">
                        <div className="admin-form-field full">
                            <label>Local Pickup Label (shown at checkout)</label>
                            <input name="localPickupLabel" value={form.localPickupLabel} onChange={handleChange} placeholder="Local Pickup (Puerto Rico)" />
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                .toggle-switch { position: relative; display: inline-block; width: 48px; height: 26px; flex-shrink: 0; }
                .toggle-switch input { opacity: 0; width: 0; height: 0; }
                .toggle-slider { position: absolute; cursor: pointer; inset: 0; background: #ccc; border-radius: 26px; transition: .4s; }
                .toggle-slider:before { position: absolute; content: ""; height: 20px; width: 20px; left: 3px; bottom: 3px; background: white; border-radius: 50%; transition: .4s; }
                .toggle-switch input:checked + .toggle-slider { background: var(--green-primary); }
                .toggle-switch input:checked + .toggle-slider:before { transform: translateX(22px); }
            `}</style>
        </div>
    );
}
